import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { BookingsPageRole } from 'src/store/bookings/types';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface MyBookingsPageRoleContextValue {
  role: BookingsPageRole | undefined;
  setRole: (role: BookingsPageRole) => void;
}

export const MyBookingsPageRoleContext = createContext<
  MyBookingsPageRoleContextValue | undefined
>(undefined);
MyBookingsPageRoleContext.displayName = 'MyBookingsPageRoleContext';

interface MyBookingsPageRoleProviderProps {
  children?: ReactNode;
}

export const MyBookingsPageRoleProvider: FC<
  MyBookingsPageRoleProviderProps
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = (searchParams.get('role') ?? undefined) as
    | BookingsPageRole
    | undefined;

  const setRole = useCallback(
    (value: BookingsPageRole) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('role', value);
        return next;
      });
      dispatch(sidesheetActions.close());
    },
    [dispatch, setSearchParams],
  );

  const contextValue = useMemo((): MyBookingsPageRoleContextValue => {
    return {
      role,
      setRole,
    };
  }, [role, setRole]);

  return (
    <MyBookingsPageRoleContext.Provider value={contextValue}>
      {children}
    </MyBookingsPageRoleContext.Provider>
  );
};

export const useMyBookingsPageRole = (): MyBookingsPageRoleContextValue =>
  useRequiredContext(MyBookingsPageRoleContext);
